if (!customElements.get('custom-content-video-card')) {
  class CustomContentVideoCard extends HTMLElement {
    static activeAudioCard = null;

    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.hoverPlay = this.dataset.hoverPlay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopHover = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
      this.abortController = new AbortController();
      this.isAudioActive = false;
      this.isMuted = true;
      this.isPlaying = false;
      this.isHoverPreview = false;
      this.loadingPromise = null;
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.deferredMedia = this.querySelector('deferred-media');
      this.section = this.querySelector('.video-section');
      this.interactionButton = this.querySelector('[data-video-action="toggle"]');
      const signal = this.abortController.signal;

      this.interactionButton.addEventListener('click', () => this.handleInteraction(), { signal });
      this.addEventListener('pointerenter', () => this.handlePointerEnter(), { signal });
      this.addEventListener('pointerleave', () => this.handlePointerLeave(), { signal });
      document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), { signal });
      this.reducedMotion.addEventListener('change', () => this.handleMotionPreference(), { signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.handleIntersection(entry));
        }, { threshold: [0, 0.6] });
        this.observer.observe(this);
      } else {
        this.inViewport = true;
      }
      this.updateUi();
    }

    disconnectedCallback() {
      this.abortController.abort();
      if (this.observer) this.observer.disconnect();
      if (CustomContentVideoCard.activeAudioCard === this) {
        CustomContentVideoCard.activeAudioCard = null;
      }
    }

    async load(trigger = 'observer') {
      if (this.playerReady()) return this.component;
      if (this.loadingPromise) return this.loadingPromise;

      this.loadingPromise = new Promise((resolve) => {
        if (!this.deferredMedia.hasAttribute('loaded')) {
          this.deferredMedia.loadContent(false, false, trigger);
        }

        const startedAt = performance.now();
        const checkPlayer = () => {
          this.component = this.querySelector('video-component');
          if (this.playerReady()) {
            this.bindNativeEvents();
            resolve(this.component);
          } else if (performance.now() - startedAt > 6000) {
            this.loadingPromise = null;
            resolve(this.component);
          } else {
            window.setTimeout(checkPlayer, 50);
          }
        };
        checkPlayer();
      });

      return this.loadingPromise;
    }

    playerReady() {
      if (!this.component || !this.component.player) return false;
      if (this.component.type === 'youtube') return typeof this.component.player.playVideo === 'function';
      if (this.component.type === 'vimeo') return typeof this.component.player.play === 'function';
      return this.component.player instanceof HTMLVideoElement;
    }

    bindNativeEvents() {
      if (!this.playerReady() || this.component.type !== 'tag' || this.nativeEventsBound) return;
      this.nativeEventsBound = true;
      const player = this.component.player;
      const signal = this.abortController.signal;

      player.addEventListener('play', () => {
        this.isPlaying = true;
        this.updateUi();
      }, { signal });
      player.addEventListener('pause', () => {
        this.isPlaying = false;
        this.updateUi();
      }, { signal });
      player.addEventListener('volumechange', () => {
        this.isMuted = player.muted || player.volume === 0;
        this.updateUi();
      }, { signal });
      player.addEventListener('ended', () => {
        this.isPlaying = false;
        this.updateUi();
      }, { signal });
    }

    handleIntersection(entry) {
      this.inViewport = entry.isIntersecting && entry.intersectionRatio >= 0.6;

      if (this.inViewport && this.autoplay && !this.reducedMotion.matches && !this.isAudioActive) {
        this.playMuted('observer');
      } else if (!entry.isIntersecting) {
        this.deactivate({ reset: true });
      }
    }

    handlePointerEnter() {
      if (!this.desktopHover.matches || !this.hoverPlay || this.reducedMotion.matches
          || this.autoplay || this.isAudioActive) return;
      this.isHoverPreview = true;
      this.playMuted('hover');
    }

    handlePointerLeave() {
      if (!this.isHoverPreview || this.isAudioActive) return;
      this.isHoverPreview = false;
      this.deactivate({ reset: true });
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.deactivate({ reset: false });
      } else if (this.inViewport && this.autoplay && !this.reducedMotion.matches) {
        this.playMuted('observer');
      }
    }

    handleMotionPreference() {
      if (this.reducedMotion.matches && !this.isAudioActive) {
        this.deactivate({ reset: true });
      } else if (this.inViewport && this.autoplay) {
        this.playMuted('observer');
      }
    }

    async playMuted(trigger) {
      await this.load(trigger);
      if (!this.playerReady() || this.isAudioActive) return;
      await this.setMuted(true);
      await this.play();
    }

    async activateAudio() {
      const previousCard = CustomContentVideoCard.activeAudioCard;
      if (previousCard && previousCard !== this) {
        await previousCard.releaseAudio();
      }

      CustomContentVideoCard.activeAudioCard = this;
      this.isAudioActive = true;
      this.isHoverPreview = false;
      this.classList.add('is-audio-active');

      await this.load('click');
      if (!this.playerReady()) return;
      await this.setMuted(false);
      await this.play();
    }

    async releaseAudio() {
      this.isAudioActive = false;
      this.classList.remove('is-audio-active');
      await this.setMuted(true);

      if (this.autoplay && this.inViewport && !this.reducedMotion.matches) {
        await this.play();
      } else {
        await this.deactivate({ reset: true });
      }
    }

    async handleInteraction() {
      if (!this.playerReady()) await this.load('click');
      if (!this.playerReady()) return;

      if (await this.paused()) {
        await this.activateAudio();
      } else if (!this.isAudioActive || this.isMuted) {
        await this.activateAudio();
      } else {
        await this.pause();
      }
    }

    async play() {
      if (!this.playerReady()) return;
      try {
        this.component.pausedByUser = false;
        if (this.component.type === 'youtube') {
          this.component.player.playVideo();
        } else {
          await this.component.player.play();
        }
        this.section.classList.add('video-section--played');
        this.isPlaying = true;
      } catch (error) {
        this.isPlaying = false;
      }
      this.updateUi();
    }

    async pause() {
      if (!this.playerReady()) return;
      if (this.component.type === 'youtube') {
        this.component.player.pauseVideo();
      } else {
        await this.component.player.pause();
      }
      this.isPlaying = false;
      this.updateUi();
    }

    async paused() {
      if (!this.playerReady()) return true;
      if (this.component.type === 'youtube') {
        return this.component.player.getPlayerState() !== 1;
      }
      if (this.component.type === 'vimeo') {
        return this.component.player.getPaused();
      }
      return this.component.player.paused;
    }

    async setMuted(muted) {
      if (!this.playerReady()) return;
      if (this.component.type === 'youtube') {
        if (muted) this.component.player.mute();
        else this.component.player.unMute();
      } else if (this.component.type === 'vimeo') {
        await this.component.player.setVolume(muted ? 0 : 1);
      } else {
        this.component.player.muted = muted;
      }
      this.isMuted = muted;
      this.updateUi();
    }

    async resetPlayer() {
      if (!this.playerReady()) return;
      if (this.component.type === 'youtube') {
        this.component.player.seekTo(0, false);
      } else if (this.component.type === 'vimeo') {
        await this.component.player.setCurrentTime(0);
      } else {
        this.component.player.currentTime = 0;
      }
    }

    async deactivate({ reset }) {
      if (!this.playerReady()) return;
      await this.setMuted(true);
      await this.pause();
      if (reset) {
        await this.resetPlayer();
        this.section.classList.remove('video-section--played');
      }

      this.isAudioActive = false;
      this.isHoverPreview = false;
      this.classList.remove('is-audio-active');
      if (CustomContentVideoCard.activeAudioCard === this) {
        CustomContentVideoCard.activeAudioCard = null;
      }
      this.updateUi();
    }

    updateUi() {
      this.classList.toggle('is-media-playing', this.isPlaying);
      this.classList.toggle('is-media-paused', !this.isPlaying && this.playerReady());
      this.classList.toggle('is-muted', this.isMuted);
      let actionLabel = 'Play video';
      if (this.isPlaying && this.isAudioActive && !this.isMuted) {
        actionLabel = 'Pause video';
      } else if (this.isPlaying) {
        actionLabel = 'Play video with sound';
      }
      this.interactionButton.setAttribute('aria-label', actionLabel);
    }
  }

  customElements.define('custom-content-video-card', CustomContentVideoCard);
}
